/**
 * Alcance global de métricas (dashboard ↔ reportes).
 * Fuente en localStorage (F5 y nueva sesión) con migración desde sessionStorage y claves legacy.
 */

export const METRICS_SCOPE_KEY = 'panelclinicas.metricsScope';
/** @deprecated migrado a METRICS_SCOPE_KEY */
export const LEGACY_DASHBOARD_FILTERS_KEY = 'panelclinicas_dashboard_filters';
/** @deprecated migrado a METRICS_SCOPE_KEY */
export const LEGACY_REPORTS_SCOPE_KEY = 'panelclinicas.reports.scope';

export const DATA_FLOOR = { year: 2023, month: 1 };

export type MetricsGranularity = 'month' | 'year';

export interface MetricsScope {
  version: 1;
  /** Período listo para reportes: siempre YYYY-MM. */
  reportPeriod: string;
  /** Clínica primaria para reportes single-tenant. */
  clinicId: string;
  /** Vista del dashboard. */
  granularity: MetricsGranularity;
  /** Período mostrado en dashboard: YYYY-MM o YYYY. */
  dashboardPeriod: string;
  /** Selección multi-clínica del dashboard (`[]` = todas). */
  clinicIds: string[];
  siteId: string;
}

export interface DashboardFiltersSlice {
  granularity: MetricsGranularity;
  period: string;
  clinicIds: string[];
  siteId: string;
}

export interface ReportNavScope {
  period?: string;
  year?: number;
  month?: number;
  tenantId?: string;
}

export function defaultReportPeriod(): { year: number; month: number } {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function defaultReportPeriodValue(): string {
  const { year, month } = defaultReportPeriod();
  return formatYearMonth(year, month);
}

export function formatYearMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function parseYearMonth(period: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

export interface ParsedDashboardPeriod {
  granularity: MetricsGranularity;
  dashboardPeriod: string;
  reportPeriod: string;
  year: number;
  month: number;
}

/** Acepta `YYYY-MM` (mes) o `YYYY` (vista anual → último mes cubierto). */
export function parseDashboardPeriod(value: string): ParsedDashboardPeriod | null {
  const ym = parseYearMonth(value);
  if (ym) {
    const dashboardPeriod = formatYearMonth(ym.year, ym.month);
    return {
      granularity: 'month',
      dashboardPeriod,
      reportPeriod: dashboardPeriod,
      year: ym.year,
      month: ym.month,
    };
  }
  if (/^\d{4}$/.test(value)) {
    const year = Number(value);
    if (Number.isInteger(year) && year >= DATA_FLOOR.year) {
      const { toMonth } = yearMonthCoverage(year);
      return {
        granularity: 'year',
        dashboardPeriod: String(year),
        reportPeriod: formatYearMonth(year, toMonth),
        year,
        month: toMonth,
      };
    }
  }
  return null;
}

/** Query de período para APIs (mes único o rango YTD/anual). */
export interface ApiPeriodQuery {
  year: number;
  month?: number;
  fromMonth?: number;
  toMonth?: number;
}

export function toApiPeriodQuery(parsed: ParsedDashboardPeriod): ApiPeriodQuery {
  if (parsed.granularity === 'year') {
    const { fromMonth, toMonth } = yearMonthCoverage(parsed.year);
    return { year: parsed.year, fromMonth, toMonth };
  }
  return { year: parsed.year, month: parsed.month };
}

/** Mes anterior, o el mismo tramo (from–to) del año previo. */
export function previousApiPeriodQuery(query: ApiPeriodQuery): ApiPeriodQuery {
  if (query.fromMonth !== undefined && query.toMonth !== undefined) {
    return { year: query.year - 1, fromMonth: query.fromMonth, toMonth: query.toMonth };
  }
  const month = query.month ?? 1;
  if (month <= 1) {
    return { year: query.year - 1, month: 12 };
  }
  return { year: query.year, month: month - 1 };
}

export function isYearPeriodQuery(query: ApiPeriodQuery): boolean {
  return query.fromMonth !== undefined && query.toMonth !== undefined;
}

/** Misma cobertura YTD/cerrado que el dashboard (último mes completo). */
export function yearMonthCoverage(year: number): { fromMonth: number; toMonth: number; isYtd: boolean } {
  const now = new Date();
  const lastComplete = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const fromMonth = year === DATA_FLOOR.year ? DATA_FLOOR.month : 1;
  const toMonth = year < lastComplete.getFullYear() ? 12 : lastComplete.getMonth() + 1;
  return { fromMonth, toMonth, isYtd: year === lastComplete.getFullYear() };
}

/**
 * Mapea el período del dashboard a un mes de reportes.
 * Vista año → último mes del tramo cubierto (YTD o dic.).
 */
export function reportPeriodFromDashboard(granularity: MetricsGranularity, period: string): string {
  if (granularity === 'month') {
    const parsed = parseYearMonth(period);
    if (parsed) {
      return formatYearMonth(parsed.year, parsed.month);
    }
  }
  if (granularity === 'year') {
    const year = Number(period);
    if (Number.isInteger(year) && year >= DATA_FLOOR.year) {
      const { toMonth } = yearMonthCoverage(year);
      return formatYearMonth(year, toMonth);
    }
  }
  return defaultReportPeriodValue();
}

function emptyScope(): MetricsScope {
  const reportPeriod = defaultReportPeriodValue();
  return {
    version: 1,
    reportPeriod,
    clinicId: '',
    granularity: 'month',
    dashboardPeriod: reportPeriod,
    clinicIds: [],
    siteId: '',
  };
}

function readStorageRaw(key: string): string | null {
  try {
    const local = localStorage.getItem(key);
    if (local) {
      return local;
    }
  } catch {
    // ignore
  }
  try {
    const session = sessionStorage.getItem(key);
    if (session) {
      try {
        localStorage.setItem(key, session);
      } catch {
        // ignore quota / private mode
      }
      return session;
    }
  } catch {
    // ignore
  }
  return null;
}

function readJson(key: string): unknown {
  try {
    const raw = readStorageRaw(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  const raw = JSON.stringify(value);
  try {
    localStorage.setItem(key, raw);
  } catch {
    // ignore quota / private mode
  }
  try {
    sessionStorage.setItem(key, raw);
  } catch {
    // ignore quota / private mode
  }
}

function normalizeScope(partial: Partial<MetricsScope> | null | undefined): MetricsScope {
  const base = emptyScope();
  if (!partial) {
    return base;
  }
  const granularity: MetricsGranularity = partial.granularity === 'year' ? 'year' : 'month';
  const dashboardPeriod =
    typeof partial.dashboardPeriod === 'string' && partial.dashboardPeriod
      ? partial.dashboardPeriod
      : typeof partial.reportPeriod === 'string' && partial.reportPeriod
        ? partial.reportPeriod
        : base.dashboardPeriod;
  const reportPeriod =
    typeof partial.reportPeriod === 'string' && parseYearMonth(partial.reportPeriod)
      ? partial.reportPeriod
      : reportPeriodFromDashboard(granularity, dashboardPeriod);

  return {
    version: 1,
    reportPeriod,
    clinicId: typeof partial.clinicId === 'string' ? partial.clinicId : '',
    granularity,
    dashboardPeriod,
    clinicIds: Array.isArray(partial.clinicIds)
      ? partial.clinicIds.filter((id): id is string => typeof id === 'string')
      : [],
    siteId: typeof partial.siteId === 'string' ? partial.siteId : '',
  };
}

/** Migra desde claves legacy si aún no existe el scope unificado. */
export function readMetricsScope(): MetricsScope {
  const unified = readJson(METRICS_SCOPE_KEY);
  if (unified && typeof unified === 'object') {
    return normalizeScope(unified as Partial<MetricsScope>);
  }

  const legacyDash = readJson(LEGACY_DASHBOARD_FILTERS_KEY) as Partial<DashboardFiltersSlice> | null;
  const legacyReport = readJson(LEGACY_REPORTS_SCOPE_KEY) as {
    period?: string;
    clinicId?: string;
  } | null;

  if (!legacyDash && !legacyReport) {
    return emptyScope();
  }

  const granularity: MetricsGranularity = legacyDash?.granularity === 'year' ? 'year' : 'month';
  const dashboardPeriod =
    (typeof legacyDash?.period === 'string' && legacyDash.period) ||
    (typeof legacyReport?.period === 'string' && legacyReport.period) ||
    '';
  const reportPeriod =
    (typeof legacyReport?.period === 'string' && parseYearMonth(legacyReport.period)
      ? legacyReport.period
      : null) || reportPeriodFromDashboard(granularity, dashboardPeriod || defaultReportPeriodValue());

  const scope = normalizeScope({
    reportPeriod,
    clinicId: typeof legacyReport?.clinicId === 'string' ? legacyReport.clinicId : '',
    granularity,
    dashboardPeriod: dashboardPeriod || reportPeriod,
    clinicIds: Array.isArray(legacyDash?.clinicIds) ? legacyDash.clinicIds : [],
    siteId: typeof legacyDash?.siteId === 'string' ? legacyDash.siteId : '',
  });

  writeMetricsScope(scope);
  return scope;
}

export function writeMetricsScope(scope: MetricsScope): void {
  const normalized = normalizeScope(scope);
  writeJson(METRICS_SCOPE_KEY, normalized);
  // Espejo legacy para no romper código residual.
  writeJson(LEGACY_DASHBOARD_FILTERS_KEY, {
    granularity: normalized.granularity,
    period: normalized.dashboardPeriod,
    clinicIds: normalized.clinicIds,
    siteId: normalized.siteId,
  });
  if (normalized.reportPeriod) {
    writeJson(LEGACY_REPORTS_SCOPE_KEY, {
      period: normalized.reportPeriod,
      clinicId: normalized.clinicId,
    });
  }
}

/** Persistencia cuando cambia el dashboard. */
export function persistDashboardFilters(filters: DashboardFiltersSlice): MetricsScope {
  const prev = readMetricsScope();
  const reportPeriod = reportPeriodFromDashboard(filters.granularity, filters.period);
  const clinicId =
    filters.clinicIds.length === 1
      ? filters.clinicIds[0]
      : prev.clinicId && filters.clinicIds.includes(prev.clinicId)
        ? prev.clinicId
        : filters.clinicIds[0] ?? prev.clinicId;

  const next: MetricsScope = {
    version: 1,
    reportPeriod,
    clinicId: clinicId ?? '',
    granularity: filters.granularity,
    dashboardPeriod: filters.period,
    clinicIds: filters.clinicIds,
    siteId: filters.siteId,
  };
  writeMetricsScope(next);
  return next;
}

/**
 * Persistencia cuando cambia el filtro de reportes.
 * Período report → dashboard en modo mes. Multi-clínica del dash se conserva.
 */
export function persistReportScope(period: string, clinicId: string): MetricsScope {
  const prev = readMetricsScope();
  const parsed = parseDashboardPeriod(period);
  const monthPeriod = parsed?.reportPeriod ?? defaultReportPeriodValue();
  const granularity = parsed?.granularity ?? 'month';
  const dashboardPeriod = parsed?.dashboardPeriod ?? monthPeriod;
  const keepMulti = prev.clinicIds.length > 1;
  const next: MetricsScope = {
    version: 1,
    reportPeriod: monthPeriod,
    clinicId: clinicId || prev.clinicId,
    granularity,
    dashboardPeriod,
    clinicIds: keepMulti ? prev.clinicIds : clinicId ? [clinicId] : prev.clinicIds,
    siteId: keepMulti ? prev.siteId : '',
  };
  writeMetricsScope(next);
  return next;
}

/**
 * Cambia solo el periodo global. Conserva clínica / multi-clínica / sede.
 */
export function persistGlobalPeriod(period: string): MetricsScope {
  const prev = readMetricsScope();
  const parsed = parseDashboardPeriod(period);
  if (!parsed) {
    return prev;
  }
  const next: MetricsScope = {
    ...prev,
    version: 1,
    reportPeriod: parsed.reportPeriod,
    granularity: parsed.granularity,
    dashboardPeriod: parsed.dashboardPeriod,
  };
  writeMetricsScope(next);
  return next;
}

export function toDashboardFilters(scope: MetricsScope = readMetricsScope()): DashboardFiltersSlice {
  return {
    granularity: scope.granularity,
    period: scope.dashboardPeriod,
    clinicIds: scope.clinicIds,
    siteId: scope.siteId,
  };
}

export function buildScopeSearchParams(scope: ReportNavScope): URLSearchParams {
  const params = new URLSearchParams();
  if (scope.period) {
    params.set('period', scope.period);
  }
  if (scope.tenantId) {
    params.set('tenant', scope.tenantId);
  }
  if (scope.year !== undefined) {
    params.set('year', String(scope.year));
  }
  if (scope.month !== undefined) {
    params.set('month', String(scope.month));
  }
  return params;
}

/** Agrega/merge query de alcance a una ruta de reportes. */
export function withReportScope(path: string, scope: ReportNavScope): string {
  const [base, existing] = path.split('?');
  const params = new URLSearchParams(existing ?? '');
  const extra = buildScopeSearchParams(scope);
  extra.forEach((value, key) => {
    params.set(key, value);
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function navScopeFromMetricsScope(
  scope: MetricsScope = readMetricsScope(),
  options?: { tenantId?: string }
): ReportNavScope {
  const parsed = parseYearMonth(scope.reportPeriod);
  return {
    period: scope.reportPeriod,
    year: parsed?.year,
    month: parsed?.month,
    tenantId: options?.tenantId ?? (scope.clinicId || undefined),
  };
}
